import React, { useState, useEffect } from 'react';
import { X, Image, Type, Music, Video, Send } from 'lucide-react';
import { POI } from '../services/poiService';
import { useAuth } from '../contexts/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MobileCardPreview from './MobileCardPreview';

interface CreateCardPanelProps {
  selectedPOI: POI | null;
  onClose: () => void;
  onSubmit: (cardData: any) => Promise<void>;
}

type ContentType = 'text' | 'image' | 'video' | 'audio';

const CreateCardPanel: React.FC<CreateCardPanelProps> = ({ selectedPOI, onClose, onSubmit }) => {
  const { user } = useAuth();
  const [contentType, setContentType] = useState<ContentType>('text');
  const [textContent, setTextContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [showCreatorInfo, setShowCreatorInfo] = useState(false);
  const [selectedSocial, setSelectedSocial] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user's social media links
  const socialLinks = user?.socialMediaLinks || [];

  useEffect(() => {
    // Reset form when POI changes
    setTextContent('');
    setImageUrl('');
    setVideoUrl('');
    setAudioUrl('');
    setShowCreatorInfo(false);
    setSelectedSocial('');
    setError(null);
  }, [selectedPOI]);

  const handleSubmit = async () => {
    if (!selectedPOI) {
      setError('Please select a POI first');
      return;
    }

    if (!textContent.trim()) {
      setError('Please add some content');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const cardData: any = {
        poi_id: selectedPOI.id,
        content: textContent,
        content_type: contentType,
      };

      // Add media URLs based on content type
      if (contentType === 'image' && imageUrl) {
        cardData.image_url = imageUrl;
      } else if (contentType === 'video' && videoUrl) {
        cardData.video_url = videoUrl;
      } else if (contentType === 'audio' && audioUrl) {
        cardData.audio_url = audioUrl;
        // For audio, we can also add an image
        if (imageUrl) {
          cardData.image_url = imageUrl;
        }
      }

      // Add creator info if enabled (user_id is set automatically by backend from JWT)
      if (showCreatorInfo && selectedSocial && socialLinks.find(link => link.platform === selectedSocial)) {
        cardData.creator_social_platform = selectedSocial;
      }

      await onSubmit(cardData);

      // Reset form on success
      setTextContent('');
      setImageUrl('');
      setVideoUrl('');
      setAudioUrl('');
      setShowCreatorInfo(false);
      setSelectedSocial('');
    } catch (err: any) {
      console.error('Error creating card:', err);
      setError(err.message || 'Failed to create card');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSocialUrl = (platform: string) => {
    const link = socialLinks.find(l => l.platform === platform);
    return link?.url || '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-creator-surface dark:bg-gray-900 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex">
        {/* Left Side - Form */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-serif font-bold text-creator-text dark:text-gray-100">
                Create New Card
              </h2>
              {selectedPOI && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  For: {selectedPOI.name}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Content Type Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-creator-text dark:text-gray-200 mb-3">
              Content Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { type: 'text' as ContentType, icon: Type, label: 'Text' },
                { type: 'image' as ContentType, icon: Image, label: 'Image' },
                { type: 'video' as ContentType, icon: Video, label: 'Video' },
                { type: 'audio' as ContentType, icon: Music, label: 'Audio' },
              ].map(({ type, icon: Icon, label }) => (
                <button
                  key={type}
                  onClick={() => setContentType(type)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    contentType === type
                      ? 'border-creator-accent bg-creator-accent bg-opacity-10 text-creator-accent'
                      : 'border-creator-border dark:border-gray-700 text-gray-500 hover:border-creator-accent'
                  }`}
                >
                  <Icon className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Media URL Inputs */}
          {contentType === 'image' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-creator-text dark:text-gray-200 mb-2">
                Image URL
              </label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-2 border border-creator-border dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-creator-accent bg-white dark:bg-gray-800 text-creator-text dark:text-gray-100"
              />
            </div>
          )}

          {contentType === 'video' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-creator-text dark:text-gray-200 mb-2">
                Video URL
              </label>
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-4 py-2 border border-creator-border dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-creator-accent bg-white dark:bg-gray-800 text-creator-text dark:text-gray-100"
              />
            </div>
          )}

          {contentType === 'audio' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-creator-text dark:text-gray-200 mb-2">
                  Audio URL
                </label>
                <input
                  type="url"
                  value={audioUrl}
                  onChange={(e) => setAudioUrl(e.target.value)}
                  placeholder="https://example.com/audio.mp3"
                  className="w-full px-4 py-2 border border-creator-border dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-creator-accent bg-white dark:bg-gray-800 text-creator-text dark:text-gray-100"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-creator-text dark:text-gray-200 mb-2">
                  Cover Image URL (Optional)
                </label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/cover.jpg"
                  className="w-full px-4 py-2 border border-creator-border dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-creator-accent bg-white dark:bg-gray-800 text-creator-text dark:text-gray-100"
                />
              </div>
            </>
          )}

          {/* Text Editor */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-creator-text dark:text-gray-200 mb-2">
              Card Content
            </label>
            <ReactQuill
              theme="snow"
              value={textContent}
              onChange={setTextContent}
              placeholder="Write your card content here..."
              className="bg-white dark:bg-gray-800 rounded-lg"
              modules={{
                toolbar: [
                  ['bold', 'italic', 'underline'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link'],
                  ['clean'],
                ],
              }}
            />
          </div>

          {/* Creator Info Toggle */}
          <div className="mb-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showCreatorInfo}
                onChange={(e) => setShowCreatorInfo(e.target.checked)}
                className="w-4 h-4 text-creator-accent focus:ring-creator-accent rounded"
              />
              <span className="text-sm font-medium text-creator-text dark:text-gray-200">
                Show Creator Information
              </span>
            </label>
          </div>

          {/* Social Media Selector */}
          {showCreatorInfo && socialLinks.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-creator-text dark:text-gray-200 mb-2">
                Display Social Media Link
              </label>
              <select
                value={selectedSocial}
                onChange={(e) => setSelectedSocial(e.target.value)}
                className="w-full px-4 py-2 border border-creator-border dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-creator-accent bg-white dark:bg-gray-800 text-creator-text dark:text-gray-100"
              >
                <option value="">None</option>
                {socialLinks.map((link) => (
                  <option key={link.platform} value={link.platform}>
                    {link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedPOI}
            className="w-full bg-creator-accent hover:bg-opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-all shadow-md flex items-center justify-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Create Card</span>
              </>
            )}
          </button>
        </div>

        {/* Right Side - Preview */}
        <div className="w-96 bg-creator-bg dark:bg-gray-800 p-6 border-l border-creator-border dark:border-gray-700 overflow-y-auto">
          <h3 className="text-sm font-semibold text-creator-text dark:text-gray-200 mb-4 text-center">
            Live Preview
          </h3>
          <MobileCardPreview
            content={textContent}
            imageUrl={imageUrl}
            videoUrl={videoUrl}
            audioUrl={audioUrl}
            contentType={contentType}
            showCreatorInfo={showCreatorInfo}
            creatorName={user?.displayName || user?.firstName || 'Creator'}
            socialPlatform={selectedSocial}
            socialUrl={getSocialUrl(selectedSocial)}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateCardPanel;
